import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";
import {
  ledgersApi,
  type TierDistributionAllocation,
  type TierSubscriberRow,
} from "@/api/ledgers";
import { Card, Button, Input } from "@/design-system/components/ui";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  downloadCsv,
  formatDate,
  formatMoney,
  formatPct,
} from "./ledgerHelpers";

/**
 * Payment System v3.0 PR 11 — Tier Ledger (design spec §10.7). One tier of
 * one pool: subscribers, monthly distributions, pro-rata allocations.
 */
export function TierLedgerPage() {
  const { id: poolId, tier } = useParams<{ id: string; tier: string }>();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const query = useQuery({
    queryKey: ["ledger.tier", poolId, tier, from, to],
    queryFn: () =>
      ledgersApi.tier(poolId!, tier!, {
        from: from || undefined,
        to: to || undefined,
      }),
    enabled: !!poolId && !!tier,
  });
  const data = query.data;

  const subscriberCols: Column<TierSubscriberRow>[] = [
    {
      key: "investor",
      header: "Subscriber",
      render: (s) => s.investorDisplayName ?? s.investorId,
    },
    { key: "face", header: "Face", render: (s) => formatMoney(s.faceAmount) },
    {
      key: "outstanding",
      header: "Current Outstanding",
      render: (s) => formatMoney(s.currentOutstanding),
    },
    { key: "share", header: "Share %", render: (s) => formatPct(s.sharePct) },
  ];

  const allocationCols: Column<TierDistributionAllocation>[] = [
    {
      key: "investor",
      header: "Subscriber",
      render: (a) => a.investorDisplayName ?? a.investorId,
    },
    { key: "face", header: "Face", render: (a) => formatMoney(a.faceAmount) },
    { key: "share", header: "Share %", render: (a) => formatPct(a.sharePct) },
    { key: "amount", header: "Allocated", render: (a) => formatMoney(a.amount) },
  ];

  function exportCsv() {
    if (!data) return;
    const rows: (string | number | null | undefined)[][] = [
      ...data.subscribers.map((s) => [
        "Subscriber",
        s.investorDisplayName ?? s.investorId,
        s.faceAmount,
        s.currentOutstanding,
        s.sharePct,
        "",
        "",
      ]),
      ...data.distributions.flatMap((d) =>
        d.allocations.map((a) => [
          "Distribution",
          a.investorDisplayName ?? a.investorId,
          a.faceAmount,
          "",
          a.sharePct,
          `${d.periodStart} → ${d.periodEnd}`,
          a.amount,
        ]),
      ),
    ];
    downloadCsv(
      `tier-ledger-${data.poolName ?? data.poolId}-${tier}.csv`,
      ["Section", "Subscriber", "Face", "Outstanding", "Share %", "Period", "Amount"],
      rows,
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Ledger"
        title={`Tier ${tier?.toUpperCase()} — ${data?.poolName ?? poolId}`}
        description="Per-tier subscriber list with pro-rata math, plus monthly distribution history."
        actions={
          <Button onClick={exportCsv} disabled={!data}>
            <Download size={16} aria-hidden /> Export CSV
          </Button>
        }
      />

      <Card className="mb-space-4">
        <div className="grid grid-cols-2 gap-space-3 mb-space-3">
          <label className="flex flex-col gap-space-1 text-small">
            <span className="text-caption uppercase">From</span>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="flex flex-col gap-space-1 text-small">
            <span className="text-caption uppercase">To</span>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
        </div>
        {data && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-space-3 text-small">
            <Totals label="Total Tier Face" value={data.totalTierFace} />
            <Totals label="Total Outstanding" value={data.totalOutstanding} />
            <Totals label="YTD Net Distributions" value={data.ytdDistributionsNet} />
          </div>
        )}
      </Card>

      <Card className="mb-space-4">
        <div className="text-body font-semibold mb-space-3">Subscribers</div>
        <DataTable
          columns={subscriberCols}
          rows={data?.subscribers ?? []}
          loading={query.isLoading}
          error={query.isError ? (query.error as Error).message : null}
          emptyMessage="No subscribers in this tier."
          rowKey={(s) => s.commitmentId}
        />
      </Card>

      <div className="text-body font-semibold mb-space-3">Distribution History</div>
      {(data?.distributions ?? []).map((d) => {
        const example = d.allocations[0];
        const exampleLabel = example
          ? `Tier total ${formatMoney(d.tierTotal)}; ${example.investorDisplayName ?? "subscriber"} has ${formatMoney(example.faceAmount)} face = ${formatPct(example.sharePct)} -> ${formatMoney(example.amount)}.`
          : "No allocations.";
        return (
          <Card key={`${d.periodStart}-${d.periodEnd}`} className="mb-space-3">
            <div className="flex items-center justify-between mb-space-2">
              <div>
                <div className="text-caption uppercase text-gray-mid">Period</div>
                <div className="text-body font-semibold">
                  {formatDate(d.periodStart)} → {formatDate(d.periodEnd)}
                </div>
              </div>
              <div className="text-right">
                <div className="text-caption uppercase text-gray-mid">Tier Total</div>
                <div className="text-body font-semibold">{formatMoney(d.tierTotal)}</div>
              </div>
            </div>
            <div className="text-caption text-gray-mid mb-space-2">{exampleLabel}</div>
            <DataTable
              columns={allocationCols}
              rows={d.allocations}
              emptyMessage="No allocations for this period."
              rowKey={(a) => a.commitmentId}
            />
          </Card>
        );
      })}
      {data && data.distributions.length === 0 && (
        <Card>
          <div className="text-small text-gray-mid">No distribution history for the selected range.</div>
        </Card>
      )}
    </>
  );
}

function Totals({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border border-border-light p-space-3 bg-white-warm">
      <div className="text-caption uppercase text-gray-mid">{label}</div>
      <div className="text-body font-semibold">{formatMoney(value)}</div>
    </div>
  );
}
